// apps/console/src/services/api/sos-missed.ts
//
// The missed list: every open critical alarm that rang out with nobody answering it.
//
// DERIVED FROM THE STORE, never held in a queue of its own. The store is what every feed (snapshot,
// poll, push) keeps current, and it is console-wide, so the dashboard's corner pill and the bell
// drawer read the same list from the same source. An alert leaves the list exactly when it leaves
// the store: someone called back or dismissed it (both acknowledge it), here or on another console.

import type { RobotAlertItem } from './robot-alerts-store'
import type { SosCovers, SosQueueState } from './sos-alert-queue'
import { sosAlertIsMissed, sosRecencyOf } from './sos-alert-timeout'

/**
 * Which held alerts belong in the missed list, newest first.
 *
 * The shared clocks decide (the server's `missedAt`, the alarm's own age). `rangOutIds` adds what a
 * dashboard's card let go by THIS machine's clock before they agree, so leaving the card never means
 * leaving the screen. Membership of the ringing queue is deliberately NOT a reason to leave an alarm
 * out: a queue left behind on another page has no sweep running, and trusting it would hide a
 * missed SOS from the bell for as long as the operator stays away from that dashboard.
 */
export function selectMissedSosAlerts(
  alerts: readonly RobotAlertItem[],
  queue: Pick<SosQueueState, 'rangOutIds'> | null,
  now: number,
  covers?: SosCovers,
): RobotAlertItem[] {
  return alerts
    .filter(a => a.severity === 'critical' && a.status === 'open')
    .filter(a => !covers || covers(a.robotId))
    .filter(a => sosAlertIsMissed(a, now) || Boolean(queue?.rangOutIds.has(a.alertId)))
    .sort((x, y) => (sosRecencyOf(y) || 0) - (sosRecencyOf(x) || 0))
}
